import { Building2, CalendarDays, Calculator, FileText, Wallet, Cloud, Sparkles, Users, BookMarked, BookOpen, ScrollText, type LucideIcon } from "lucide-react";
import { tokens } from "../styles";

/** Onglets affichés sur la page Paramètres entreprise (URL `?onglet=`). */
export type ParametresEntrepriseTab =
  | "generaux"
  | "exercice"
  | "fiscaux"
  | "liasse"
  | "facturation"
  | "tresorerie"
  | "transmission"
  | "regles"
  | "journaux"
  | "equipe";

export type ParametresNavTab = ParametresEntrepriseTab | "plan_comptable";

export interface ParametresSidebarProps {
  activeTab: ParametresEntrepriseTab | "plan_comptable";
  onTabChange: (tab: ParametresNavTab) => void;
  dossierNom: string;
  /** Réservé propriétaire — masque l’onglet Journaux si false. */
  showJournauxTab?: boolean;
}

const NAV_ITEMS: {
  tab: ParametresNavTab;
  label: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  requiresJournaux?: boolean;
}[] = [
  { tab: "generaux", label: "Informations générales", Icon: Building2, iconBg: "#dbeafe", iconColor: "#2563eb" },
  { tab: "exercice", label: "Exercice comptable", Icon: CalendarDays, iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { tab: "fiscaux", label: "Paramètres fiscaux", Icon: Calculator, iconBg: "#fef3c7", iconColor: "#d97706" },
  { tab: "liasse", label: "Liasse fiscale", Icon: ScrollText, iconBg: "#cffafe", iconColor: "#0e7490" },
  { tab: "facturation", label: "Facturation", Icon: FileText, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { tab: "tresorerie", label: "Trésorerie", Icon: Wallet, iconBg: "#ccfbf1", iconColor: "#0d9488" },
  { tab: "transmission", label: "Transmission", Icon: Cloud, iconBg: "#e0e7ff", iconColor: "#4f46e5" },
  { tab: "regles", label: "Centre de règles", Icon: Sparkles, iconBg: "#fce7f3", iconColor: "#db2777" },
  { tab: "journaux", label: "Journaux", Icon: BookMarked, iconBg: "#fff7ed", iconColor: "#ea580c", requiresJournaux: true },
  { tab: "equipe", label: "Équipe", Icon: Users, iconBg: "#e0f2fe", iconColor: "#0369a1" },
  { tab: "plan_comptable", label: "Plan comptable", Icon: BookOpen, iconBg: "#ffedd5", iconColor: "#ea580c" },
];

export function ParametresSidebar({ activeTab, onTabChange, dossierNom, showJournauxTab = true }: ParametresSidebarProps) {
  return (
    <nav
      aria-label="Paramètres du dossier"
      style={{
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        flex: 1,
        background: tokens.sidebarBg,
        borderRadius: 0,
        overflow: "hidden",
        position: "relative",
        padding: "16px 12px 20px",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'DM Sans', 'IBM Plex Sans', system-ui, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(120,60,180,0.14) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 240,
          background: "linear-gradient(0deg, rgba(160,40,100,0.28) 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      <div style={{ padding: "0 6px 17px", borderBottom: "1px solid rgba(255,255,255,0.07)", marginBottom: 16, position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)", marginBottom: 2 }}>{dossierNom}</div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            color: "rgba(255,255,255,0.35)",
          }}
        >
          Paramètres
        </div>
      </div>

      <ul
        className="sidebar-nav-scroll"
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        {NAV_ITEMS.filter((item) => !(item.requiresJournaux && !showJournauxTab)).map(({ tab, label }) => {
          const active = activeTab === tab;
          return (
            <li key={tab}>
              <button
                type="button"
                onClick={() => onTabChange(tab)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7.5px 14px 7.5px 10px",
                  borderRadius: 8,
                  border: "none",
                  borderLeft: active ? "2px solid oklch(74% 0.18 345)" : "2px solid transparent",
                  background: active ? "rgba(255,255,255,0.08)" : "transparent",
                  color: active ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.50)",
                  fontSize: 12.5,
                  fontWeight: active ? 500 : 400,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  transition: "all 0.15s",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: active ? "oklch(74% 0.18 345)" : "rgba(255,255,255,0.20)",
                    boxShadow: active ? "0 0 6px oklch(74% 0.18 345 / .6)" : "none",
                  }}
                />
                {label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
